/**
 * CORE-W-04: the Conversion Actions block, extracted from
 * AdsManagementTab so the WhatConverts tab can render the same data.
 *
 * Spec §6 (WhatConverts) folds Conversion Actions into this tab: "the
 * single home for anything conversion-related — the lead list, contacts,
 * conversion actions, values and scoring, all of it." §2.10 pulls the
 * block off Management (CORE-M-10.b).
 *
 * The panel is the shared innards — the summary warning, the annotate
 * table, the annotate dialog. Callers wrap it in whatever container they
 * prefer (CollapsibleCard on Management, a plain Card on WhatConverts).
 *
 * ⛔ THE READ IS GATED ON read_ok. A failed read is not an empty result;
 * showing an empty table on a failure is the same defect that told a
 * client they had zero spend in a month they spent thousands.
 *
 * ⚠ The `Annotate` action stays flag-gated on `conversion_override_write`.
 * The dialog writes operator-side notes (category correction, tier,
 * free-form notes) that never touch Google Ads.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertCircle, Info, TriangleAlert } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { AdsWaitState } from '@/components/google-ads/AdsWaitState';
import {
  ManagementTable,
  type ManagementColumn,
} from '@/components/google-ads/ManagementTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  canWrite,
  getConversionView,
  updateConversionActionOverride,
  type ConversionActionRow,
} from '@/services/googleAdsManagementService';

export interface ConversionActionsPanelProps {
  tenantId: string | null;
  /** Trailing window in days for the conversion read; defaults to 30. */
  days?: number;
}

export function ConversionActionsPanel({
  tenantId,
  days = 30,
}: ConversionActionsPanelProps) {
  const queryClient = useQueryClient();
  const conversions = useQuery({
    // ⚠ Match the AdsManagementTab queryKey exactly so a page that
    // renders both surfaces (during the M-10.b transition) makes one
    // network read, not two.
    queryKey: ['ga-conversion-view', tenantId, days],
    queryFn: () => getConversionView(tenantId ?? '', days),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });

  const [editingAction, setEditingAction] =
    useState<ConversionActionRow | null>(null);
  const [overrideForm, setOverrideForm] = useState({
    category_override: '',
    notes: '',
    tier: '',
  });
  // CORE-W-04.b bulk state. `bulkTargets` non-null puts the SAME dialog in
  // bulk mode; `clearTargets` is the Clear-annotation confirm, which always
  // asks before writing because it destroys notes rather than adding them.
  const [bulkTargets, setBulkTargets] = useState<ConversionActionRow[] | null>(null);
  const [clearTargets, setClearTargets] = useState<ConversionActionRow[] | null>(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkSummary, setBulkSummary] = useState<BulkSummary | null>(null);

  const openBulkEditor = (rows: ConversionActionRow[]) => {
    setBulkSummary(null);
    setOverrideForm({ category_override: '', notes: '', tier: '' });
    setBulkTargets(rows);
  };

  const closeBulkEditor = () => {
    if (bulkRunning) return;
    setBulkTargets(null);
  };

  /**
   * Write the same override to every selected action, then refetch ONCE.
   * Per-row so one failure cannot take the batch down; the summary names
   * the rows that did not make it so the operator can retry those.
   */
  const runBulkOverride = async (
    rows: ConversionActionRow[],
    payload: { category_override: string; notes: string; tier: string },
  ) => {
    setBulkRunning(true);
    setBulkSummary(null);
    const body = toOverrideBody(payload);
    // ⚠ Chunked, not one Promise.allSettled over the whole selection.
    // Selection survives paging and the page size reaches 50, so an
    // uncapped fan-out is 50 simultaneous POSTs, each holding a backend DB
    // session against a pool of 20 (+10 overflow) — the tail would come
    // back as "N failed" for nothing worse than an eager operator.
    const failed: string[] = [];
    for (let i = 0; i < rows.length; i += BULK_WRITE_CHUNK) {
      const chunk = rows.slice(i, i + BULK_WRITE_CHUNK);
      const results = await Promise.allSettled(
        chunk.map((row) =>
          updateConversionActionOverride(tenantId ?? '', row.resource_name, body),
        ),
      );
      // Failures accumulate across chunks; one slow chunk never hides an
      // earlier failure, and the summary still names every row that missed.
      chunk.forEach((row, j) => {
        if (results[j].status === 'rejected') failed.push(row.name);
      });
    }
    setBulkRunning(false);
    setBulkTargets(null);
    setClearTargets(null);
    setBulkSummary({ total: rows.length, failed });
    if (failed.length === 0) {
      toast.success(`Annotated ${rows.length}`);
    } else {
      toast.error(
        `Annotated ${rows.length - failed.length} of ${rows.length}; ${failed.length} failed`,
      );
    }
    // One refetch for the whole batch, not one per row.
    queryClient.invalidateQueries({ queryKey: ['ga-conversion-view'] });
  };

  const openOverrideEditor = (action: ConversionActionRow) => {
    setEditingAction(action);
    setOverrideForm({
      category_override: action.category_override ?? '',
      notes: action.notes ?? '',
      tier: action.tier ?? '',
    });
  };

  const overrideMutation = useMutation({
    mutationFn: (vars: {
      resourceName: string;
      category_override: string;
      notes: string;
      tier: string;
    }) =>
      updateConversionActionOverride(
        tenantId ?? '',
        vars.resourceName,
        toOverrideBody(vars),
      ),
    onSuccess: () => {
      toast.success('Annotation saved.');
      setEditingAction(null);
      queryClient.invalidateQueries({ queryKey: ['ga-conversion-view'] });
    },
    onError: () => {
      toast.error('Could not save the annotation.');
    },
  });

  const conversionColumns = useMemo<
    ManagementColumn<ConversionActionRow>[]
  >(
    () => [
      {
        key: 'name',
        header: 'Action',
        width: 260,
        sort: (a) => a.name.toLowerCase(),
        render: (a) => (
          <>
            {a.name}
            {a.is_gbp_local && a.primary_for_goal && (
              <Badge className="ml-2" variant="destructive">
                not a lead
              </Badge>
            )}
          </>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        width: 120,
        sort: (a) => a.status,
        render: (a) => (
          <Badge variant={a.status === 'ENABLED' ? 'secondary' : 'outline'}>
            {a.status || 'unknown'}
          </Badge>
        ),
      },
      {
        key: 'bidding',
        header: 'Bidding',
        width: 120,
        sort: (a) => (a.primary_for_goal ? 'Primary' : 'Secondary'),
        render: (a) => (
          // ⛔ Primary is the consequential state: primary actions are what
          // bidding optimises towards, so a wrong one moves money.
          <Badge variant={a.primary_for_goal ? 'default' : 'outline'}>
            {a.primary_for_goal ? 'Primary' : 'Secondary'}
          </Badge>
        ),
      },
      {
        key: 'conversions_30d',
        header: 'Last 30d',
        width: 110,
        align: 'right',
        sort: (a) => a.conversions_30d,
        render: (a) =>
          // ⚠ Google returns fractions (611.444885). Rounding to one decimal
          // stops the column looking broken.
          a.conversions_30d.toLocaleString(undefined, {
            maximumFractionDigits: 1,
          }),
      },
      {
        key: 'concern',
        header: 'Note',
        width: 260,
        sort: (a) => a.concern,
        render: (a) =>
          a.is_silent ? (
            <span className="text-destructive">{a.concern}</span>
          ) : (
            <span className="text-muted-foreground">{a.concern}</span>
          ),
      },
      {
        // ⚠ category_override never overwrites category — both are shown, so
        // a correction reads as a diff against what Google actually reports.
        key: 'annotation',
        header: 'Category / tier',
        width: 220,
        render: (a) => (
          <div className="space-y-0.5">
            <div>
              {a.category_override ? (
                <>
                  <span className="font-medium">{a.category_override}</span>
                  <span className="ml-1 text-xs text-muted-foreground">
                    (overridden from: {a.category || 'unknown'})
                  </span>
                </>
              ) : (
                <span>
                  {a.category || (
                    <span className="text-muted-foreground">—</span>
                  )}
                </span>
              )}
              {a.notes && (
                <span
                  title={a.notes}
                  aria-label="Has notes"
                  className="inline-flex"
                >
                  <Info
                    className="ml-1 inline h-3.5 w-3.5 text-muted-foreground"
                    aria-hidden
                  />
                </span>
              )}
            </div>
            {a.tier && <Badge variant="outline">{a.tier}</Badge>}
          </div>
        ),
      },
      {
        key: 'actions',
        // CORE-W-04.b: 130, not 90. ManagementTable gives a non-fill cell
        // `width/minWidth/maxWidth: <width>` plus TableCell's own `p-4`
        // (16px each side) and `overflow-hidden`, so the usable box is
        // `width - 32`. The Annotate button is ~92px (px-3 + border +
        // "Annotate" at text-sm), so 90 left 58px and cut the button's
        // right edge off mid-control. Anything under ~124 clips again.
        header: '',
        width: 130,
        render: (a) =>
          canWrite(conversions.data?.capabilities, 'conversion_override_write') ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => openOverrideEditor(a)}
            >
              Annotate
            </Button>
          ) : null,
      },
    ],
    [conversions.data?.capabilities],
  );

  if (conversions.isLoading) {
    return <AdsWaitState stage="Reading from Google Ads" />;
  }
  // ⚠ A failed read is NOT an empty result. Say so, and say what it means.
  if (!conversions.data || !conversions.data.read_ok) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-coral/40 bg-coral-light/20 p-3">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-coral-dark" />
        <div className="text-sm">
          <p className="font-medium">Could not read this from Google Ads.</p>
          <p className="text-muted-foreground">
            {conversions.data?.message ||
              'This is not the same as "nothing to report". Treat anything below as unknown.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {conversions.data.gbp_primary_count > 0 && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <p className="text-sm">{conversions.data.message}</p>
        </div>
      )}
      {bulkSummary && (
        <div
          role="status"
          className={
            'mb-3 rounded-md border p-3 text-sm ' +
            (bulkSummary.failed.length === 0
              ? 'border-border bg-muted/40 text-muted-foreground'
              : 'border-destructive/40 bg-destructive/5 text-destructive dark:border-red-700/60 dark:bg-red-950/50 dark:text-red-200')
          }
        >
          {bulkSummary.failed.length === 0 ? (
            <p>Annotated {bulkSummary.total}</p>
          ) : (
            <>
              <p>
                Annotated {bulkSummary.total - bulkSummary.failed.length} of{' '}
                {bulkSummary.total}; {bulkSummary.failed.length} failed
              </p>
              <p className="mt-1">Not saved: {bulkSummary.failed.join(', ')}</p>
            </>
          )}
        </div>
      )}
      <ManagementTable<ConversionActionRow>
        rows={conversions.data.actions ?? []}
        columns={conversionColumns}
        getRowId={(a) => a.id}
        bulkActions={
          canWrite(conversions.data?.capabilities, 'conversion_override_write')
            ? [
                {
                  label: 'Annotate selected',
                  onRun: (rows) => openBulkEditor(rows),
                },
                {
                  label: 'Clear annotation',
                  variant: 'outline' as const,
                  onRun: (rows) => {
                    setBulkSummary(null);
                    setClearTargets(rows);
                  },
                },
              ]
            : []
        }
        searchFields={(a) => [a.name, a.status, a.concern]}
        filters={[
          {
            key: 'bidding',
            label: 'Bidding',
            value: (a) => (a.primary_for_goal ? 'Primary' : 'Secondary'),
          },
          { key: 'status', label: 'Status', value: (a) => a.status },
        ]}
        defaultSortKey="conversions_30d"
        defaultSortDir="desc"
        searchPlaceholder="Search conversion actions…"
        emptyText="No conversion actions on this account. Nothing is being measured."
        rowTone={(a) =>
          a.is_gbp_local && a.primary_for_goal
            ? 'bad'
            : a.is_silent
              ? 'warn'
              : ''
        }
      />

      <Dialog
        open={editingAction !== null || bulkTargets !== null}
        onOpenChange={(open) => {
          if (open) return;
          if (bulkTargets !== null) closeBulkEditor();
          else setEditingAction(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {bulkTargets
                ? `Annotate ${bulkTargets.length} conversion actions`
                : `Annotate: ${editingAction?.name}`}
            </DialogTitle>
            <DialogDescription>
              Operator notes only — this is never sent to Google Ads. The
              category correction shows next to what Google actually reports;
              it does not change Google's record.
            </DialogDescription>
          </DialogHeader>
          {bulkTargets && (
            <p className="text-sm text-muted-foreground">
              This sets the same category, tier and note on all{' '}
              {bulkTargets.length} selected actions.
            </p>
          )}
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="conv-category-override">Category override</Label>
              <Input
                id="conv-category-override"
                value={overrideForm.category_override}
                onChange={(e) =>
                  setOverrideForm((f) => ({
                    ...f,
                    category_override: e.target.value,
                  }))
                }
                placeholder={
                  bulkTargets ? 'e.g. Qualified Lead' : editingAction?.category || 'e.g. Qualified Lead'
                }
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="conv-tier">Tier</Label>
              <Input
                id="conv-tier"
                value={overrideForm.tier}
                onChange={(e) =>
                  setOverrideForm((f) => ({ ...f, tier: e.target.value }))
                }
                placeholder="e.g. gold"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="conv-notes">Notes</Label>
              <Textarea
                id="conv-notes"
                value={overrideForm.notes}
                onChange={(e) =>
                  setOverrideForm((f) => ({ ...f, notes: e.target.value }))
                }
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                bulkTargets ? closeBulkEditor() : setEditingAction(null)
              }
              disabled={overrideMutation.isPending || bulkRunning}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (bulkTargets) {
                  void runBulkOverride(bulkTargets, overrideForm);
                  return;
                }
                if (editingAction) {
                  overrideMutation.mutate({
                    resourceName: editingAction.resource_name,
                    ...overrideForm,
                  });
                }
              }}
              disabled={overrideMutation.isPending || bulkRunning}
            >
              {overrideMutation.isPending || bulkRunning ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CORE-W-04.b: clearing wipes notes someone wrote, so it always
          confirms and names the count first. */}
      <AlertDialog
        open={clearTargets !== null}
        onOpenChange={(open) => {
          if (!open && !bulkRunning) setClearTargets(null);
        }}
      >
        {/* The shared AlertDialog wrapper renders no landmark role of its
            own; AlertDialogContent forwards extra props to its div, so the
            role and label are set here for assistive tech. */}
        <AlertDialogContent role="alertdialog" aria-modal="true" aria-label="Clear annotation">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Clear the annotation on {clearTargets?.length ?? 0} conversion
              actions?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This removes the category correction, tier and notes an operator
              wrote for each selected action. What Google Ads reports is not
              affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setClearTargets(null)}
              disabled={bulkRunning}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (clearTargets) {
                  void runBulkOverride(clearTargets, {
                    category_override: '',
                    notes: '',
                    tier: '',
                  });
                }
              }}
              disabled={bulkRunning}
            >
              Clear annotation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/** CORE-W-04.b: what the last bulk write produced. */
type BulkSummary = { total: number; failed: string[] };

/** How many override writes are in flight at once during a bulk run. */
const BULK_WRITE_CHUNK = 5;

/**
 * The one place the override form becomes a request body: trimmed, and
 * blank means "cleared" (null), not an empty string. Shared by the per-row
 * mutation and the bulk runner so the two cannot drift.
 */
function toOverrideBody(form: {
  category_override: string;
  notes: string;
  tier: string;
}): { category_override: string | null; notes: string | null; tier: string | null } {
  return {
    category_override: form.category_override.trim() || null,
    notes: form.notes.trim() || null,
    tier: form.tier.trim() || null,
  };
}

export default ConversionActionsPanel;
